// Admin Customer Hooks

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminCustomerApi } from '../../api/adminCustomerApi';
import type { CustomerListQuery } from '../../types/customer.types';

// Query keys for cache management
export const customerQueryKeys = {
  all: ['admin', 'customers'] as const,
  lists: () => [...customerQueryKeys.all, 'list'] as const,
  list: (params: CustomerListQuery) => [...customerQueryKeys.lists(), params] as const,
  details: () => [...customerQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerQueryKeys.details(), id] as const,
  segmentCounts: () => [...customerQueryKeys.all, 'counts'] as const,
  tags: () => [...customerQueryKeys.all, 'tags'] as const,
};

/** Hook to fetch paginated list of customers with filtering */
export function useCustomerList(params: CustomerListQuery = {}) {
  return useQuery({
    queryKey: customerQueryKeys.list(params),
    queryFn: () => adminCustomerApi.listCustomers(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/** Hook to fetch a single customer by ID */
export function useCustomer(id: string) {
  return useQuery({
    queryKey: customerQueryKeys.detail(id),
    queryFn: () => adminCustomerApi.getCustomer(id),
    enabled: !!id,
    staleTime: 60 * 1000, // 1 minute
  });
}

/** Hook to fetch customer segment counts for dashboard */
export function useCustomerSegmentCounts() {
  return useQuery({
    queryKey: customerQueryKeys.segmentCounts(),
    queryFn: () => adminCustomerApi.getSegmentCounts(),
    staleTime: 60 * 1000, // 1 minute
  });
}

/** Hook to fetch all available tags */
export function useCustomerTags() {
  return useQuery({
    queryKey: customerQueryKeys.tags(),
    queryFn: () => adminCustomerApi.getAllTags(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/** Hook to update customer status */
export function useUpdateCustomerStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive, reason }: { id: string; isActive: boolean; reason?: string }) =>
      adminCustomerApi.updateCustomerStatus(id, isActive, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.detail(variables.id) });
    },
  });
}

/** Hook to add a note to a customer */
export function useAddCustomerNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      content,
      isImportant,
    }: {
      customerId: string;
      content: string;
      isImportant?: boolean;
    }) => adminCustomerApi.addNote(customerId, content, isImportant),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.detail(variables.customerId) });
    },
  });
}

/** Hook to delete a customer note */
export function useDeleteCustomerNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, noteId }: { customerId: string; noteId: string }) =>
      adminCustomerApi.deleteNote(customerId, noteId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.detail(variables.customerId) });
    },
  });
}

/** Hook to add a tag to a customer */
export function useAddCustomerTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, tag, color }: { customerId: string; tag: string; color?: string }) =>
      adminCustomerApi.addTag(customerId, tag, color),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.detail(variables.customerId) });
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.tags() });
    },
  });
}

/** Hook to remove a tag from a customer */
export function useRemoveCustomerTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, tag }: { customerId: string; tag: string }) =>
      adminCustomerApi.removeTag(customerId, tag),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.detail(variables.customerId) });
    },
  });
}
